// Hover/focus/touch/visibility prefetch for lazy route chunks.
// Reduces perceived latency when navigating between pages via in-page links.
//
// We keep an explicit path → importer map (only for common navigation targets)
// instead of trying to derive it from the route tree. Adding a route here is
// optional — pages not listed just behave as today (load on click).
//
// Importers MUST be the exact same dynamic import() call used by App.tsx's
// lazyRoute(), so Vite reuses the same chunk and the second resolve is a no-op.

type Importer = () => Promise<unknown>;

const importers: Record<string, Importer> = {
  "/": () => import("@/pages/Index.tsx"),
  "/about": () => import("@/pages/About.tsx"),
  "/mission": () => import("@/pages/Mission.tsx"),
  "/memberships": () => import("@/pages/Memberships.tsx"),
  "/access": () => import("@/pages/Access.tsx"),
  "/access/coaching": () => import("@/pages/AccessCoaching.tsx"),
  "/access/support": () => import("@/pages/AccessSupport.tsx"),
  "/squadup": () => import("@/pages/SquadUp.tsx"),
  "/start": () => import("@/pages/StartHub.tsx"),
  "/featured": () => import("@/pages/Featured.tsx"),
  "/free-training": () => import("@/pages/FreeTraining.tsx"),
  "/discord-help": () => import("@/pages/DiscordHelp.tsx"),
  "/join-discord": () => import("@/pages/JoinDiscord.tsx"),
  "/contact": () => import("@/pages/Contact.tsx"),
  "/blog": () => import("@/pages/Blog.tsx"),
  "/games": () => import("@/pages/TacticalGamesRanked.tsx"),
  "/fronts": () => import("@/pages/Fronts.tsx"),
  "/ranks": () => import("@/pages/RanksAccolades.tsx"),
  "/leaderboard": () => import("@/pages/Accolades.tsx"),
  "/clans": () => import("@/pages/ClanDirectory.tsx"),
  "/find-a-unit": () => import("@/pages/FindAUnit.tsx"),
  "/profile": () => import("@/pages/MyProfile.tsx"),
  "/my-teams": () => import("@/pages/MySquad.tsx"),
  "/events": () => import("@/pages/Events.tsx"),
  "/calendar": () => import("@/pages/CommunityCalendar.tsx"),
  "/lfg": () => import("@/pages/SquadFinder.tsx"),
  "/recap": () => import("@/pages/WeeklyRecap.tsx"),
  "/playbook": () => import("@/pages/Playbook.tsx"),
  "/operator-profile": () => import("@/pages/OperatorProfile.tsx"),
  "/operator-path": () => import("@/pages/OperatorPath.tsx"),
  "/help": () => import("@/pages/Help.tsx"),
  "/faq": () => import("@/pages/FAQ.tsx"),
  "/how-it-works": () => import("@/pages/HowItWorks.tsx"),
  "/find-teammates": () => import("@/pages/FindTeammates.tsx"),
  "/tonight": () => import("@/pages/Tonight.tsx"),
  "/play-now": () => import("@/pages/PlayNow.tsx"),
  "/solo": () => import("@/pages/Solo.tsx"),
  "/shop": () => import("@/pages/Shop.tsx"),
  "/why-tgh": () => import("@/pages/WhyTGH.tsx"),
  "/clan-wars": () => import("@/pages/ClanWars.tsx"),
  // Network / directory pages — heavily cross-linked, prefetch aggressively.
  "/network": () => import("@/pages/JoinTheNetwork.tsx"),
  "/esports": () => import("@/pages/Esports.tsx"),
  "/milsim": () => import("@/pages/Milsim.tsx"),
  "/creators": () => import("@/pages/CreatorsDirectory.tsx"),
  "/allied-communities": () => import("@/pages/AlliedCommunities.tsx"),
  "/devs": () => import("@/pages/DevsPitch.tsx"),
  // Shop / apparel / gear / courses / prizes / lexicon — commonly slow chunks.
  "/shop/apparel": () => import("@/pages/ShopCategoryComingSoon.tsx"),
  "/shop/gear": () => import("@/pages/ShopCategoryComingSoon.tsx"),
  "/courses": () => import("@/pages/TacticalLoadouts.tsx"),
  "/tacticalloadouts": () => import("@/pages/TacticalLoadouts.tsx"),
  "/prizes": () => import("@/pages/Prizes.tsx"),
  "/glossary": () => import("@/pages/RanksGlossary.tsx"),
};


// Prefix → importer map for dynamic-param routes (e.g. /blog/:slug, /games/:game).
// When a link's path doesn't match `importers` exactly, we fall back to these.
const prefixImporters: { prefix: string; importer: Importer }[] = [
  { prefix: "/blog/", importer: () => import("@/pages/BlogArticle.tsx") },
  { prefix: "/games/", importer: () => import("@/pages/GamePage.tsx") },
  { prefix: "/clans/", importer: () => import("@/pages/ClanPage.tsx") },
  { prefix: "/members/", importer: () => import("@/pages/MemberProfilePage.tsx") },
  { prefix: "/member/", importer: () => import("@/pages/MemberProfilePage.tsx") },
];


const prefetched = new Set<string>();

const normalize = (href: string): string | null => {
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    // Strip trailing slash (except root) and query/hash for lookup.
    let path = url.pathname;
    if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
    return path;
  } catch {
    return null;
  }
};

export const prefetchByPath = (path: string) => {
  if (prefetched.has(path)) return;
  const importer =
    importers[path] ?? prefixImporters.find((p) => path.startsWith(p.prefix))?.importer;
  if (!importer) return;
  prefetched.add(path);
  // Fire-and-forget; swallow errors (offline, chunk gone, etc.)
  importer().catch(() => prefetched.delete(path));
};

let installed = false;

export const installLinkPrefetch = () => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const handler = (event: Event) => {
    const target = event.target as Element | null;
    if (!target) return;
    const anchor = target.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!anchor) return;
    const href = anchor.getAttribute("href");
    if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
    const path = normalize(anchor.href);
    if (path) prefetchByPath(path);
  };

  document.addEventListener("pointerover", handler, { capture: true, passive: true });
  document.addEventListener("focusin", handler, { capture: true, passive: true });
  document.addEventListener("touchstart", handler, { capture: true, passive: true });

  // Prefetch links as they scroll into view — makes navigation feel instant
  // even without hover (e.g. on mobile or fast keyboard users).
  if ("IntersectionObserver" in window) {
    const seen = new WeakSet<Element>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const anchor = entry.target as HTMLAnchorElement;
          const path = normalize(anchor.href);
          if (path) prefetchByPath(path);
          io.unobserve(anchor);
        }
      },
      { rootMargin: "200px" }
    );

    const observeAll = () => {
      document.querySelectorAll<HTMLAnchorElement>("a[href^='/']").forEach((a) => {
        if (seen.has(a)) return;
        seen.add(a);
        io.observe(a);
      });
    };

    // Initial scan + re-scan on route changes / DOM mutations (debounced).
    let pending = false;
    const schedule = () => {
      if (pending) return;
      pending = true;
      requestIdleCallback?.(() => {
        pending = false;
        observeAll();
      }) ?? setTimeout(() => {
        pending = false;
        observeAll();
      }, 250);
    };
    schedule();
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true });
  }
};
